import { CheckCircle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

interface SuccessModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  submessage?: string;
  onClose: () => void;
  buttonText?: string;
  highlightText?: string;
}

export function SuccessModal({ 
  isOpen, 
  title, 
  message, 
  submessage, 
  onClose, 
  buttonText = "Continue",
  highlightText 
}: SuccessModalProps) {
  if (!isOpen) return null;

  // Function to render message with highlighted text
  const renderMessage = () => {
    if (!highlightText) return message;
    
    const parts = message.split(highlightText);
    return (
      <>
        {parts[0]}
        <span className="font-semibold text-orange-600">{highlightText}</span>
        {parts[1]}
      </>
    );
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]" 
      style={{ pointerEvents: 'auto' }}
      onClick={(e) => {
        // Only close if clicking the backdrop, not the modal content
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl" 
        style={{ pointerEvents: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative mb-6">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto relative">
            <CheckCircle className="w-12 h-12 text-green-600" />
            <div className="absolute -top-2 -right-2">
              <Sparkles className="w-8 h-8 text-yellow-500 animate-pulse" />
            </div>
          </div>
        </div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">
          {title}
        </h3>
        <p className="text-slate-600 mb-4">
          {renderMessage()}
        </p>
        {submessage && (
          <p className="text-sm text-slate-500">
            {submessage}
          </p>
        )}
        <div className="mt-8">
          <Button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            variant="primary"
            className="w-full h-12 cursor-pointer"
            type="button"
          >
            {buttonText}
          </Button>
        </div>
      </div>
    </div>
  );
}
